import { Component, EventEmitter, inject, OnDestroy, Output } from '@angular/core';
import { CostPlanFormItemComponent } from '../cost-plan-form-item/cost-plan-form-item.component';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CostBase, CurrencyMap, CurrencySymbolMap, EmployeeLevel, EmployeeLocation, EmployeeType, PlanCost, PlanFormStatus, Role, RoleConfig } from '../types';
import { CostPlanService } from '../services/cost-plan.service';
import { CurrencyService } from '../services/currency.service';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { AccordionModule } from 'primeng/accordion';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';

type SubmitDetailItem = { level: string; type: string; location: string; count: number };

@Component({
    selector: 'cost-plan-form',
    imports: [CostPlanFormItemComponent, CommonModule, ReactiveFormsModule, FormsModule, SelectModule, AccordionModule, InputTextModule, TooltipModule],
    templateUrl: './cost-plan-form.component.html',
    styleUrl: './cost-plan-form.component.less',
})
export class CostPlanFormComponent implements OnDestroy {
    private route: ActivatedRoute = inject(ActivatedRoute);
    private fb: FormBuilder = inject(FormBuilder);
    private messageService: MessageService = inject(MessageService);

    @Output() formStatusUpdate = new EventEmitter<PlanFormStatus>();

    roleList: RoleConfig[] = [];

    employeeLocations: EmployeeLocation[] = [];

    employeeLevels: EmployeeLevel[] = [];

    employeeTypes: EmployeeType[] = [];

    cweRate: number = 0;

    currentPlan: Role[] = [];

    employeeAmounts: number = 0;

    monthlyCost: PlanCost = {
        min: 0,
        max: 0,
    };

    currencyMap: CurrencyMap = { USD: 1, AUD: 1, CNY: 1 };
    currencySymbolMap: CurrencySymbolMap = { USD: '$', AUD: 'A$', CNY: '¥' };

    selectedCurrency: string = 'USD';
    monthlyCostAmount: string = '$ 0.00';
    monthlyCostAmountTooltip: string = '';
    annuallyCostAmount: string = '$ 0.00';
    annuallyCostAmountTooltip: string = '';
    hourlyCostPerPersonAmount: string = '$ 0.00';
    hourlyCostPerPersonAmountTooltip: string = '';
    isShowMoreCostPlanDetail: boolean = false;

    planForm = this.fb.group({
        email: ['', [Validators.required, Validators.email]],
        employeeAmounts: ['', Validators.required],
    });

    private destroy$ = new Subject<void>();

    constructor(
        private costPlanService: CostPlanService,
        private currencyService: CurrencyService
    ) {
        this.costPlanService
            .getCostPlanConfig()
            .pipe(takeUntil(this.destroy$))
            .subscribe((config) => {
                this.employeeLocations = config.employeeLocations;
                this.employeeTypes = config.employeeTypes;
                this.employeeLevels = config.roleLevels;
                this.roleList = config.roles;
                this.cweRate = config.cweRate || 0;
                this.initialCurrentPlan();
            });

        this.currencyService
            .getCurrencyMap()
            .pipe(takeUntil(this.destroy$))
            .subscribe((currencyData) => {
                this.currencyMap = currencyData.data;
                this.calculateCostAmount(this.selectedCurrency);
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Handles changes to the form.
     *
     * @param {Role} event The form change event.
     */
    formChange(event: Record<string, unknown>): void {
        this.currentPlan = this.currentPlan.map((item) => (item.roleName === (event as Role).roleName ? { ...item, ...event } : item));
        this.calculateCost();
    }

    /**
     * Initializes the current plan based on the selected plan type from the route parameters.
     */
    initialCurrentPlan() {
        const planType = this.route.snapshot.paramMap.get('planType') || 'customize';
        const planConfig = this.costPlanService.getDefaultPlans(planType);
        const defaultRoleOrder = planConfig.details.map((item) => item.key);
        this.currentPlan = this.sortRoleListAsDefaultOrder(this.roleList, defaultRoleOrder).map((role) => {
            const planData = planConfig.details.find((item) => item.key === role.roleName);
            return {
                roleName: role.roleName,
                list:
                    planType === 'customize' || !planData
                        ? []
                        : [
                              {
                                  level: 1,
                                  location: this.employeeLocations[0].id,
                                  type: this.employeeTypes[0].id,
                                  count: planData.count,
                                  isSelected: true,
                              },
                          ],
            };
        });
        this.calculateCost(true);
    }

    /**
     * Calculates the total cost based on the current plan.
     */
    calculateCost(isInitialTrigger: boolean = false) {
        let employeeAmounts = 0;
        const cost = { min: 0, max: 0 };
        this.currentPlan.forEach((role) => {
            const roleCostBases = this.roleList.find((r) => r.roleName === role.roleName)?.costBases || [];
            role.list.forEach((item) => {
                if (item.isSelected && this.checkRowIsValid(item)) {
                    const location: EmployeeLocation | undefined = this.employeeLocations.find((loc) => loc.id === item.location);
                    const costBase: CostBase | undefined = roleCostBases.find((base) => base.level === item.level);
                    const type: string = this.employeeTypes.find((type) => type.id === item.type)?.name || '';
                    if (!costBase || !location) {
                        console.error(`Missing cost base or location for role: ${role.roleName}, level: ${item.level}, location: ${item.location}`);
                        return;
                    }
                    const { ssi, extraSSI, markup }: EmployeeLocation = location;
                    const { minBase, maxBase }: CostBase = costBase;
                    const isCWE = type.toLowerCase() === 'cwe';
                    const rates = isCWE ? 1 + this.cweRate : 1 + ssi + extraSSI + markup;
                    cost.min = cost.min + minBase * rates * item.count;
                    cost.max = cost.max + maxBase * rates * item.count;
                    employeeAmounts += item.count;
                }
            });
        });
        this.employeeAmounts = employeeAmounts;
        this.monthlyCost = {
            min: cost.min,
            max: cost.max,
        };
        this.calculateCostAmount(this.selectedCurrency);
        this.planForm.patchValue({ employeeAmounts: `${this.employeeAmounts ? this.employeeAmounts : ''}` });
        if (!isInitialTrigger) {
            this.planForm.markAsTouched();
        }
        this.formStatusUpdate.emit({ isSubmitted: false, isTouched: this.planForm.touched, submitFn: this.requestQuote.bind(this) });
    }

    /**
     * Tracks the role list by its name.
     *
     * @param {number} _index The index of the item in the list.
     * @param {Role} item The role item.
     * @returns {string} The unique identifier for the role item.
     */
    trackByRoleName(_index: number, item: Role): string {
        return item.roleName;
    }

    /**
     * Calculates the cost amount based on the selected currency.
     *
     * @param {string} selectedCurrency The currency to calculate the cost in.
     */
    calculateCostAmount(selectedCurrency: string) {
        const rate: number = this.currencyMap[selectedCurrency];
        const { min, max }: Record<string, number> = this.monthlyCost;
        const monthHour: number = 21 * 8;
        const minCost: number = min * rate;
        const maxCost: number = max * rate;
        const minAnnuallyCostAmount = minCost * 12;
        const maxAnnuallyCostAmount = maxCost * 12;
        const employeeAmounts = this.employeeAmounts || 1; // Avoid division by zero
        if (this.employeeAmounts > 0) {
            const minHourlyCostPerPerson = minCost / employeeAmounts / monthHour;
            const maxHourlyCostPerPerson = maxCost / employeeAmounts / monthHour;
            this.monthlyCostAmount = `${this.formatCurrency(minCost)} ~ ${this.formatCurrency(maxCost, false)}`;
            this.annuallyCostAmount = `${this.formatCurrency(minAnnuallyCostAmount)} ~ ${this.formatCurrency(maxAnnuallyCostAmount, false)}`;
            this.hourlyCostPerPersonAmount = `${this.formatCurrency(minHourlyCostPerPerson)} ~ ${this.formatCurrency(maxHourlyCostPerPerson, false)}`;

            this.monthlyCostAmountTooltip = minCost > 1000 || maxCost > 1000 ? `${this.formatCurrency(minCost, true, false)} ~ ${this.formatCurrency(maxCost, true, false)}` : '';
            this.annuallyCostAmountTooltip = minAnnuallyCostAmount > 1000 || maxAnnuallyCostAmount > 1000 ? `${this.formatCurrency(minAnnuallyCostAmount, true, false)} ~ ${this.formatCurrency(maxAnnuallyCostAmount, true, false)}` : '';
            this.hourlyCostPerPersonAmountTooltip = minHourlyCostPerPerson > 1000 || maxHourlyCostPerPerson > 1000 ? `${this.formatCurrency(minHourlyCostPerPerson, true, false)} ~ ${this.formatCurrency(maxHourlyCostPerPerson, true, false)}` : '';
        } else {
            this.monthlyCostAmount = `${this.currencySymbolMap[this.selectedCurrency]} 0.00`;
            this.annuallyCostAmount = `${this.currencySymbolMap[this.selectedCurrency]} 0.00`;
            this.hourlyCostPerPersonAmount = `${this.currencySymbolMap[this.selectedCurrency]} 0.00`;

            this.monthlyCostAmountTooltip = '';
            this.annuallyCostAmountTooltip = '';
            this.hourlyCostPerPersonAmountTooltip = '';
        }
    }

    /**
     * Handles the exchange rate selection change.
     * @param {SelectChangeEvent} event The event object containing the selected currency.
     */
    onExchangeRateClick(event: SelectChangeEvent) {
        this.selectedCurrency = event.value;
        this.calculateCostAmount(this.selectedCurrency);
    }

    /**
     * Submits the current cost plan form to request a quote.
     */
    requestQuote() {
        this.planForm.markAllAsTouched();
        this.planForm.get('email')?.markAsDirty();
        if (this.planForm.invalid) {
            return;
        }
        const requestBody = {
            email: this.planForm.get('email')?.value,
            roles: this.currentPlan
                .map((role: Role) => {
                    const details = role.list
                        .filter((item) => item.isSelected && item.count && this.checkRowIsValid(item))
                        .map((item) => {
                            return {
                                level: this.getNameById(item.level, 'level'),
                                type: this.getNameById(item.type, 'type'),
                                location: this.getNameById(item.location, 'location'),
                                count: item.count,
                            };
                        });
                    return {
                        roleName: role.roleName,
                        details: this.mergeDuplicateItem(details),
                    };
                })
                .filter((role) => role.details.length > 0),
            monthlyCost: this.monthlyCostAmount,
            annuallyCost: this.annuallyCostAmount,
            hourlyCostPerPerson: this.hourlyCostPerPersonAmount,
        };
        this.costPlanService
            .postRequestQuote(requestBody)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.formStatusUpdate.emit({ isSubmitted: true, isTouched: true });
                },
                error: () => {
                    this.messageService.add({ severity: 'error', detail: 'Failed to submit the request. Please try again later.' });
                },
            });
    }

    /**
     * Toggles the visibility of more cost plan details in mobile view.
     */
    onMoreDetailsClick() {
        this.isShowMoreCostPlanDetail = !this.isShowMoreCostPlanDetail;
    }

    /**
     * Retrieves the name of an employee attribute by its ID.
     *
     * @param {number} id The ID of the employee attribute.
     * @param {'location' | 'level' | 'type'} type The type of attribute to retrieve the name for.
     * @returns {string} The name of the employee attribute.
     */
    getNameById(id: number, type: 'location' | 'level' | 'type'): string {
        switch (type) {
            case 'location':
                return this.employeeLocations.find((loc) => loc.id === id)?.hiringLocation || '';
            case 'level':
                return this.employeeLevels.find((level) => level.id === id)?.name || '';
            case 'type':
                return this.employeeTypes.find((type) => type.id === id)?.name || '';
            default:
                return '';
        }
    }

    /**
     * Formats a currency amount.
     *
     * @param {number} amount The amount to format.
     * @param {boolean} showCurrencySymbol Whether to show the currency symbol.
     * @returns {string} The formatted currency string.
     */
    formatCurrency(amount: number, showCurrencySymbol: boolean = true, isShort: boolean = true): string {
        let result = '0';
        let surfix = '';
        if (amount < 1000) {
            result = (Math.floor(amount * 100) / 100).toFixed(2).toString();
        } else if (amount < 1000000) {
            surfix = isShort ? 'K' : '';
            const sum = isShort ? amount / 1000 : amount;
            const decimals = surfix ? 1 : 100;
            const formatOptions = surfix
                ? {}
                : {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                  };
            result = new Intl.NumberFormat('en-US', formatOptions).format(Math.floor(sum * decimals) / decimals) + surfix;
        } else {
            surfix = isShort ? 'M' : '';
            const sum = isShort ? amount / 1000000 : amount;
            const decimals = surfix ? 1 : 100;
            const formatOptions = surfix
                ? {}
                : {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                  };
            result = new Intl.NumberFormat('en-US', formatOptions).format(Math.floor(sum * decimals) / decimals) + surfix;
        }
        return showCurrencySymbol ? `${this.currencySymbolMap[this.selectedCurrency]} ${result}` : `${result}`;
    }

    /**
     * Checks if a cost plan row is valid.
     *
     * @param {Record<string, number>} item The cost plan row item to check.
     * @returns {boolean} True if the row is valid, false otherwise.
     */
    private checkRowIsValid(item: Role['list'][number]): boolean {
        const level: number | null = item.level;
        const type: number | null = item.type;
        const location: number | null = item.location;
        return (level || level === 0) && (type || type === 0) && (location || location === 0) ? true : false;
    }

    /**
     * Merges duplicate items in the list by summing their counts.
     *
     * @param list The list of items to merge.
     * @returns The merged list of items.
     */
    private mergeDuplicateItem(list: SubmitDetailItem[]) {
        const merged = new Map<string, SubmitDetailItem>();
        list.forEach((item) => {
            const key = `${item.level}-${item.type}-${item.location}`;
            if (merged.has(key)) {
                merged.get(key)!.count += item.count;
            } else {
                merged.set(key, { ...item });
            }
        });
        return Array.from(merged.values());
    }

    /**
     * Sorts the role list according to the default order specified in the default array.
     *
     * @param {RoleConfig[]} roleList The list of roles to sort.
     * @param {string[]} defaultArray The default order array.
     * @returns {RoleConfig[]} The sorted list of roles.
     */
    private sortRoleListAsDefaultOrder(roleList: RoleConfig[], defaultArray: string[]): RoleConfig[] {
        if (defaultArray.length === 0) return roleList;
        const roleMap = new Map<string, RoleConfig>();
        roleList.forEach((role) => {
            roleMap.set(role.roleName, role);
        });
        const sortedList: RoleConfig[] = [];
        defaultArray.forEach((roleName) => {
            const role = roleMap.get(roleName);
            if (role) {
                sortedList.push(role);
                roleMap.delete(roleName);
            }
        });
        return sortedList.concat(Array.from(roleMap.values()));
    }
}
